import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfileUnavailable from './ProfileUnavailable';
import { 
  checkAccess, 
  AccessCheckResult,
  hasCompletedOnboarding,
  requiresApproval,
  isApproved,
  getDefaultPage,
  normalizeUserRole,
  ROLE_HIERARCHY,
  UserRole
} from '../../utils/rbac';

interface RoleGuardProps {
  children: React.ReactNode;
  /** Minimum role required to access this route */
  minRole?: UserRole;
  /** Specific roles allowed (overrides minRole) */
  allowedRoles?: UserRole[];
  /** If true, bypasses approval check */
  bypassApproval?: boolean;
  /** If true, bypasses onboarding check */
  bypassOnboarding?: boolean;
  /** Custom redirect path on access denied */
  redirectTo?: string;
}

/**
 * RoleGuard - Comprehensive route protection component
 * 
 * This component handles:
 * 1. Authentication check
 * 2. Profile loading
 * 3. Role-based access control
 * 4. Onboarding completion check
 * 5. Approval status check (for teacher/school)
 * 
 * Usage:
 * <RoleGuard allowedRoles={['admin', 'superadmin']}>
 *   <AdminPage />
 * </RoleGuard>
 */
export const RoleGuard = ({ 
  children, 
  minRole,
  allowedRoles,
  bypassApproval = false,
  bypassOnboarding = false,
  redirectTo
}: RoleGuardProps) => {
  const { user, profile, loading, profileLoading, profileError } = useAuth();
  const location = useLocation();
  const [accessResult, setAccessResult] = useState<AccessCheckResult | null>(null);

  useEffect(() => {
    // Wait for auth and profile to load
    if (loading || profileLoading) return;

    // Check access
    const result = checkAccess(profile, location.pathname, !!user);
    
    // Additional role checks if specified
    if (result.allowed && profile) {
      const normalizedRole = normalizeUserRole(profile.role);
      // Check minRole
      if (minRole) {
        if (ROLE_HIERARCHY[normalizedRole] < ROLE_HIERARCHY[minRole]) {
          setAccessResult({
            allowed: false,
            redirectTo: redirectTo || getDefaultPage(normalizedRole),
            reason: `Minimum role '${minRole}' required`
          });
          return;
        }
      }
      
      // Check allowedRoles
      if (allowedRoles && !allowedRoles.includes(normalizedRole)) {
        setAccessResult({
          allowed: false,
          redirectTo: redirectTo || getDefaultPage(normalizedRole),
          reason: `Role '${normalizedRole}' not in allowed roles`
        });
        return;
      }
    }
    
    // Override approval check if bypassed
    if (bypassApproval && result.redirectTo === '/approval-pending') {
      setAccessResult({ allowed: true });
      return;
    }
    
    // Override onboarding check if bypassed
    if (bypassOnboarding && result.redirectTo === '/onboarding') {
      setAccessResult({ allowed: true });
      return;
    }
    
    setAccessResult(result);
  }, [user, profile, loading, profileLoading, location.pathname, minRole, allowedRoles, bypassApproval, bypassOnboarding, redirectTo]);

  // Show loading state
  if (loading || profileLoading || accessResult === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-white/60">Verifying access...</p>
        </div>
      </div>
    );
  }

  // A profile we could not READ is not the same as a user with no access. Redirecting
  // here sent them to /login, which cannot route them onward without a profile either
  // — a loop that reads as "login is broken". Show the recoverable error instead.
  if (user && profileError) {
    return <ProfileUnavailable />;
  }

  // Redirect if not allowed
  if (!accessResult.allowed && accessResult.redirectTo) {
    console.log(`[RoleGuard] Access denied to ${location.pathname}. Reason: ${accessResult.reason}. Redirecting to ${accessResult.redirectTo}`);
    return <Navigate to={accessResult.redirectTo} replace />;
  }

  // Access granted
  return <>{children}</>;
};

/**
 * StudentGuard - Only allows students and higher roles
 */
export const StudentGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['student', 'teacher', 'school', 'admin', 'superadmin']}>
    {children}
  </RoleGuard>
);

/**
 * TeacherGuard - Only allows teachers and higher roles (excludes school administrators)
 * School administrators should NOT have access to Create, Explore, History
 */
export const TeacherGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['teacher', 'admin', 'superadmin']}>
    {children}
  </RoleGuard>
);

/**
 * AdminOrSchoolGuard - Only allows school, admin, superadmin (no student, teacher, principal).
 * Use for Chapter Editor / Studio / Content Library.
 */
export const AdminOrSchoolGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['school', 'admin', 'superadmin']}>
    {children}
  </RoleGuard>
);

/**
 * AdminGuard - Only allows admin and superadmin (no API Keys for school/teacher/principal)
 */
export const AdminGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['admin', 'superadmin']}>
    {children}
  </RoleGuard>
);

/**
 * StudioGuard - Allows admin, superadmin, and associate (Content Library / Chapter Editor).
 * Associate can refine lessons but cannot delete; changes require admin approval.
 */
export const StudioGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['admin', 'superadmin', 'associate']}>
    {children}
  </RoleGuard>
);

/**
 * SuperAdminGuard - Only allows superadmin
 */
export const SuperAdminGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['superadmin']}>
    {children}
  </RoleGuard>
);

/**
 * QuestionPaperGuard - Allows teachers, principals, school admins, admins, and superadmins.
 * Students are explicitly blocked from the AI Question Paper Generator.
 */
export const QuestionPaperGuard = ({ children }: { children: React.ReactNode }) => (
  <RoleGuard allowedRoles={['teacher', 'principal', 'school', 'partner', 'admin', 'superadmin']}>
    {children}
  </RoleGuard>
);

export default RoleGuard;
